/**
 * variantFitGuard.js — RC 1.49.x.TRANSIENT_SCORING Stage 2 carry-forward
 *
 * Shared sessionStorage helper for `cv_variant` Analyse Fit payloads.
 * The contract is operator-locked (`JobApplicationWizard.jsx:2093-2146`,
 * `:2251-2261`) and pre-dates the route-based workspace; this module
 * is a thin parity-mirror so AnalyseFitSection (writer) and
 * PackSection (reader) can use the SAME sessionStorage key + TTL +
 * payload shape the wizard already owns.
 *
 * Architectural context (RC 1.49.x.TRANSIENT_SCORING Stage 2,
 * operator-locked, 2026-05-06):
 *   • `cv_variant` Analyse Fit is intentionally TRANSIENT — never
 *     persisted to `job_scores`. Therefore there is no `score.id`
 *     on the wire and no FK to bind via `application_packs.analyse_fit_id`.
 *   • The variant fit data is carried via `pack_snapshot.fit`
 *     (a JSONB blob on `application_packs`) instead.
 *   • This sessionStorage guard is the wizard's mechanism for
 *     bridging the gap between the live `/analyze-fit` response
 *     and a subsequent pack-create call within the same browser
 *     session, AND for the 30-min duplicate-charge guard that
 *     prevents a double-click from running two paid fits on the
 *     same (user, job, variant) tuple.
 *
 * Strict rails (DO NOT relax):
 *   • Key is keyed by `(user_id, jobId, variant_id)` — no
 *     cross-source, no cross-job, no cross-user contamination.
 *   • TTL is 30 min (operator-locked to match the wizard's
 *     duplicate-charge guard window).
 *   • Source CV (`uploaded_cv`) NEVER writes here — that path uses
 *     the persistent `job_scores` row + `analyse_fit_id` URL triple.
 *   • Reads MUST trust the per-`(user_id, jobId, variant_id)` storage
 *     key as the source of source-scope truth. Earlier rails
 *     additionally validated `payload.billing.source_type === 'cv_variant'`
 *     and `billing.source_id === variantId`, but the wire shape from
 *     `runAnalyzeFitWithPolling` does not consistently carry those
 *     fields — the polled-async (`synthesiseCanonical`) and 504-recovery
 *     (`synthesiseCanonicalFromCachedRow`) paths both build a minimal
 *     `{ cached, was_free_fit, usage_consumed }` billing block. The
 *     pre-hotfix reader rejected every such payload, making the
 *     workspace appear to lose Analyse Fit state on re-entry for any
 *     variant fit that exceeded the 30s ingress timeout. We now rely
 *     on the storage key's own scoping (operator-locked: scoped to
 *     userId + jobId + variantId). The wizard's own reader
 *     (`JobApplicationWizard.jsx:2108-2143`) follows the same
 *     trust-the-key pattern.
 *   • Failures (sessionStorage unavailable / JSON malformed) are
 *     fail-open — the section degrades gracefully to "no transient
 *     state" rather than blocking the user.
 */

export const VARIANT_FIT_GUARD_TTL_MS = 30 * 60 * 1000; // 30 min — wizard-owned

/** Build the canonical sessionStorage key for a (user, job, variant). */
export function variantFitGuardKey(userId, jobId, variantId) {
  if (!userId || !jobId || !variantId) return null;
  return `variantFitGuard:${userId}:${jobId}:${variantId}`;
}

/**
 * Write the live `/analyze-fit` response to sessionStorage under the
 * wizard's canonical key. Same shape the wizard writes
 * (`JobApplicationWizard.jsx:2256-2259`):
 *   { cachedAt: epochMs, payload: <full /analyze-fit response> }
 *
 * Caller must guarantee `sourceType === 'cv_variant'` — the helper
 * does not write for `uploaded_cv` (that path is FK-bound via the
 * URL triple and would silently shadow the canonical row).
 *
 * Returns true on success, false on any failure (private-mode storage,
 * quota exceeded, key invalid, source mismatch).
 */
export function writeVariantFitGuard({ userId, jobId, variantId, payload }) {
  const key = variantFitGuardKey(userId, jobId, variantId);
  if (!key || !payload) return false;
  try {
    sessionStorage.setItem(key, JSON.stringify({
      cachedAt: Date.now(),
      payload,
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Read a fresh, source-scoped guard for (user, job, variant).
 * Returns the parsed `payload` (the original `/analyze-fit` response)
 * iff:
 *   • the entry exists,
 *   • `cachedAt` is within `VARIANT_FIT_GUARD_TTL_MS`,
 *   • the persisted JSON parses to an object containing a non-empty
 *     `payload` field.
 * Returns null otherwise. Never throws.
 *
 * Source scoping is enforced by the storage key itself
 * (`variantFitGuard:{userId}:{jobId}:{variantId}`); a payload written
 * for a different (user, job, variant) tuple cannot be read here
 * because the key won't match. Earlier defensive `billing.source_type`
 * / `billing.source_id` checks were removed — those wire fields are
 * absent from the polled-async / recovery synthesisers and the strict
 * check rejected every such payload (operator soak 2026-02).
 */
export function readVariantFitGuard({ userId, jobId, variantId }) {
  const key = variantFitGuardKey(userId, jobId, variantId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt) || 0;
    if (!cachedAt) return null;
    if (Date.now() - cachedAt >= VARIANT_FIT_GUARD_TTL_MS) return null;
    const payload = parsed?.payload;
    if (!payload || typeof payload !== 'object') return null;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Remove any guard for the given (user, job, variant) tuple. Used
 * by the source-change clear path in AnalyseFitSection so a stale
 * guard from a prior selection cannot bleed into the current binding.
 *
 * Note: operator-locked rule — we ONLY clear the guard for the
 * variant we are explicitly invalidating (e.g. the OLD variant the
 * user just switched away from). Other variants' guards remain
 * intact so the wizard's duplicate-charge protection survives.
 */
export function clearVariantFitGuard({ userId, jobId, variantId }) {
  const key = variantFitGuardKey(userId, jobId, variantId);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Fail-open — sessionStorage unavailable.
  }
}

/**
 * Materialise a `pack_snapshot.fit` object from a variant guard
 * payload. Mirrors the wizard's `step3LastResult.scoreRow` shape
 * (`JobApplicationWizard.jsx:2231-2242`) so a wizard-created and a
 * workspace-created pack carry an identical snapshot blob for the
 * same underlying run.
 *
 * Returns null when the payload lacks a usable `score` block.
 */
export function snapshotFitFromVariantPayload(payload) {
  const score = payload?.score;
  if (!score) return null;
  return {
    // `score_id` is intentionally null for transient variants — there
    // is no `job_scores` row. Mirrors wizard line 2129.
    score_id:        score.id ?? null,
    fit_score:       score.fit_score ?? null,
    final_decision:  score.final_decision ?? null,
    salary_score:    score.salary_score ?? null,
    seniority_score: score.seniority_score ?? null,
    location_score: score.location_score ?? null,
    strategic_score: score.strategic_score ?? null,
    risk_score:      score.risk_score ?? null,
    evaluated_at:    score.evaluated_at ?? score.created_at ?? null,
    reasoning_json:  score.reasoning_json ?? payload?.analysis ?? {},
    // Surface source identity so PackSection's source-match guard
    // can re-verify the snapshot at the moment of pack creation
    // (defence-in-depth on top of the read-time gate).
    source_type:     'cv_variant',
    source_id:       payload?.billing?.source_id ?? null,
  };
}
